import React, { useEffect, useMemo, useState } from "react";
import { Spinner, Alert, Button, Form, ToggleButton, ToggleButtonGroup, Badge } from "react-bootstrap";
import ResultRow from "./ResultRow";
import RecipientCard, { CardGrid } from "../shared/RecipientCard";
import VetToolbar from "./VetToolbar";
import { LOGIN_RECENCY_OPTIONS, DEFAULT_LOGIN_RECENCY } from "../constants";

const VIEW_MODE_DEFAULT = "cards";
const VIEW_MODE_STORAGE_KEY = "recipientPicker.viewMode";
const POST_FILTER_RESET_MAX_DISTANCE = 0.85;
const TINT_STYLE = { backgroundColor: "rgb(53 98 135 / 4%)" };

// Per-mode pre-search prompt copy. When a tab has been opened but no
// search has run there yet, the empty state tells the admin what
// they're meant to do in that specific tab.
function preSearchPromptForMode(mode) {
  switch (mode) {
    case "by_name":
      return { title: "Search for media people by name", body: "Enter a name or email above and click Search." };
    case "by_outlet":
      return {
        title: "Search for media people by outlet",
        body: "Enter an outlet name, display name, or domain above and click Search.",
      };
    default:
      return {
        title: "Search for media people",
        body: "Describe the audience above (or paste a brief) and click Search.",
      };
  }
}

function loadInitialViewMode() {
  if (typeof window === "undefined" || !window.localStorage) return VIEW_MODE_DEFAULT;
  try {
    const stored = window.localStorage.getItem(VIEW_MODE_STORAGE_KEY);
    return stored === "list" || stored === "cards" ? stored : VIEW_MODE_DEFAULT;
  } catch (_) {
    return VIEW_MODE_DEFAULT;
  }
}

// Centre column of the modal. Server returns wide results (full
// MAX_SEARCH_DISTANCE, no login_recency filter); this column applies
// max_distance and login_recency as client-side post-filters in the
// header toolbar — admins can retune without re-firing the search.
//
// Renders one of:
//   - Loading spinner
//   - Error alert (with retry affordance if retry_advised)
//   - "Search to begin" prompt
//   - "Too many outlets matched" refine prompt (by_outlet only)
//   - "No matches" empty state
//   - Result list (semantic / by_name) — flat with header toolbar
//   - Outlet-grouped result list (by_outlet) — header toolbar + per-outlet groups
//
// The header toolbar exposes: select-all toggle (filtered to non-already-
// added rows), semantic distance slider (semantic mode only),
// login-recency dropdown, and the view-mode toggle. The bottom toolbar
// carries the "X selected · N of M shown" count and the "Vet visible"
// action (which swaps to inline progress + Cancel while vetting).
// Rows that match `existingIds` are shown with an "Already added" badge.
export default function ResultsColumn({
  mode,
  results,
  checked,
  toggleCheck,
  setCheckedForIds,
  loading,
  error,
  retryAdvised,
  onRetry,
  focusedId,
  onFocus,
  searched,
  existingIds = new Set(),
  addableSelectedCount = 0,
  // Post-filter state lifted to RecipientPicker so the "Add N selected"
  // footer reflects the same visible-and-checked set the toolbar shows.
  maxDistance,
  setMaxDistance,
  loginRecency,
  setLoginRecency,
  hideExisting,
  setHideExisting,
  rowPasses,
  // AI vet. `verdicts` decorates cards + drives the call-site cache;
  // `currentDigest` is the current prompt-inputs digest — a verdict is
  // stale when its stored `vetted_against` differs from it.
  // onVetVisible(candidates) runs the vet; onCancelVet aborts.
  verdicts = {},
  currentDigest = "",
  onVetVisible,
  onCancelVet,
  vetting = false,
  vetProgress = { completed: 0, total: 0 },
  vetFailedBatches = [],
}) {
  const [viewMode, setViewMode] = useState(loadInitialViewMode);
  useEffect(() => {
    try {
      window.localStorage?.setItem(VIEW_MODE_STORAGE_KEY, viewMode);
    } catch (_) {
      // localStorage unavailable (private mode, quota, etc.) — fall back
      // to in-memory state only.
    }
  }, [viewMode]);

  const filtersActive =
    maxDistance < POST_FILTER_RESET_MAX_DISTANCE || loginRecency !== DEFAULT_LOGIN_RECENCY || hideExisting;
  const clearFilters = () => {
    setMaxDistance(POST_FILTER_RESET_MAX_DISTANCE);
    setLoginRecency(DEFAULT_LOGIN_RECENCY);
    setHideExisting(false);
  };

  // Filtered rows for semantic / by_name (flat arrays).
  // For by_outlet, filter reporters within each group and drop empty groups.
  const filteredRows = useMemo(() => {
    const rows = results.rows || [];
    if (mode === "by_outlet") {
      return rows
        .map((group) => ({
          ...group,
          reporters: (group.reporters || []).filter(rowPasses),
        }))
        .filter((group) => group.reporters.length > 0);
    }
    return rows.filter(rowPasses);
  }, [results.rows, mode, rowPasses]);

  // Flatten for select-all/header math.
  const flatRows = useMemo(
    () => filteredRows.flatMap((row) => (row.reporters ? row.reporters : [row])),
    [filteredRows],
  );
  const addableRows = useMemo(() => flatRows.filter((r) => !existingIds.has(r.id)), [flatRows, existingIds]);
  const addableIds = useMemo(() => addableRows.map((r) => r.id), [addableRows]);
  const allAddableChecked = addableIds.length > 0 && addableIds.every((id) => checked.has(id));
  const someAddableChecked = addableIds.some((id) => checked.has(id));

  // Server total for the "showing X of Y" subtitle: tail-of-response count
  // for paginated modes, or the flat sum of unfiltered reporters for by_outlet.
  const serverTotal = useMemo(() => {
    if (results.pagination?.total != null) return results.pagination.total;
    if (mode === "by_outlet") {
      return (results.rows || []).reduce((sum, g) => sum + (g.reporters?.length || 0), 0);
    }
    return (results.rows || []).length;
  }, [results, mode]);

  // Per-verdict staleness: a verdict is stale when the prompt-inputs it
  // was judged against differ from the current ones. A verdict with no
  // stored `vetted_against` (legacy / pre-feature) reads as non-stale.
  const verdictStaleFor = (id) => {
    const va = verdicts[id]?.vetted_against;
    return va != null && va !== currentDigest;
  };

  // "Vet visible" set: the rows currently rendered (post-filter), minus
  // any that already carry a *fresh* verdict — the call-site cache, so we
  // don't re-vet and re-spend on candidates we've already judged. A stale
  // verdict is eligible for re-vetting.
  //
  // Deduped by id: by_outlet can surface the same reporter under multiple
  // outlet groups, so flatRows may repeat a candidate. Vetting (and the
  // count) must treat each person once — duplicates would re-pay AI cost.
  const vettableRows = useMemo(() => {
    const seen = new Set();
    const out = [];
    flatRows.forEach((r) => {
      if (seen.has(r.id)) return;
      const v = verdicts[r.id];
      const hasFreshVerdict = v && !(v.vetted_against != null && v.vetted_against !== currentDigest);
      if (hasFreshVerdict) return;
      seen.add(r.id);
      out.push(r);
    });
    return out;
  }, [flatRows, verdicts, currentDigest]);

  if (loading) {
    return (
      <div className="d-flex align-items-center justify-content-center text-muted p-5 h-100">
        <Spinner animation="border" size="sm" className="me-2" /> Searching…
      </div>
    );
  }

  if (error) {
    return (
      <Alert variant="danger" className="m-3 py-2 px-3 small">
        {error}
        {retryAdvised && onRetry && (
          <>
            {" "}
            <Button size="sm" variant="outline-danger" className="ms-2" onClick={onRetry}>
              Retry
            </Button>
          </>
        )}
      </Alert>
    );
  }

  if (!searched) {
    const prompt = preSearchPromptForMode(mode);
    return (
      <EmptyState icon="fa-magnifying-glass" title={prompt.title}>
        {prompt.body}
      </EmptyState>
    );
  }

  if (results.pagination?.refine_required) {
    return (
      <EmptyState icon="fa-filter" title="Too many outlets matched your search">
        Refine the query to narrow it down.
      </EmptyState>
    );
  }

  if (serverTotal === 0) {
    return (
      <EmptyState icon="fa-magnifying-glass" title="No matches">
        Try a different query.
      </EmptyState>
    );
  }

  // Top toolbar: select-all + filters + view-mode. The "X selected · N of M shown"
  // summary lives in the bottom toolbar so it stays pinned to the bottom of the
  // scrolling results pane.
  const selectAllLabel = allAddableChecked ? "Deselect all" : "Select all";
  const topToolbar = (
    <div className="d-flex align-items-center gap-2 p-2 border-bottom flex-wrap flex-shrink-0" style={TINT_STYLE}>
      <Form.Check
        type="checkbox"
        id="results-select-all"
        checked={allAddableChecked}
        disabled={addableIds.length === 0}
        ref={(el) => {
          // HTMLInputElement.indeterminate has no React prop equivalent — must set on the ref.
          // eslint-disable-next-line no-param-reassign
          if (el) el.indeterminate = !allAddableChecked && someAddableChecked;
        }}
        onChange={(e) => setCheckedForIds(addableIds, e.target.checked)}
        label={<span className="small">{selectAllLabel}</span>}
      />
      {existingIds.size > 0 && (
        <Form.Check
          type="switch"
          id="results-hide-existing"
          checked={hideExisting}
          onChange={(e) => setHideExisting(e.target.checked)}
          label={<span className="small">Hide already-added</span>}
        />
      )}
      <div className="flex-grow-1" />

      {mode === "semantic" && (
        <div className="d-flex flex-column align-items-center" style={{ width: 110 }} title="Max semantic distance">
          <span
            className="text-muted"
            style={{
              fontSize: "0.65rem",
              lineHeight: 1,
              padding: "2px 6px",
              borderRadius: "0.75rem",
              backgroundColor: "var(--bs-tertiary-bg)",
              marginBottom: 2,
            }}
          >
            ≤ {maxDistance.toFixed(2)}
          </span>
          <Form.Range
            min={0.3}
            max={0.85}
            step={0.05}
            value={maxDistance}
            onChange={(e) => setMaxDistance(parseFloat(e.target.value))}
            aria-label="Max semantic distance"
            className="m-0"
          />
        </div>
      )}

      <Form.Select
        size="sm"
        value={loginRecency}
        onChange={(e) => setLoginRecency(e.target.value)}
        style={{ width: 140 }}
        aria-label="Filter by login recency"
      >
        {LOGIN_RECENCY_OPTIONS.map((opt) => (
          <option key={opt.value} value={opt.value}>
            {opt.label}
          </option>
        ))}
      </Form.Select>

      <ToggleButtonGroup
        size="sm"
        type="radio"
        name="view-mode"
        value={viewMode}
        onChange={setViewMode}
        aria-label="Results view mode"
      >
        <ToggleButton id="view-mode-cards" value="cards" variant="outline-secondary" title="Card view">
          <i className="fa-solid fa-grip" aria-hidden="true" />
        </ToggleButton>
        <ToggleButton id="view-mode-list" value="list" variant="outline-secondary" title="List view">
          <i className="fa-solid fa-list-ul" aria-hidden="true" />
        </ToggleButton>
      </ToggleButtonGroup>
    </div>
  );

  // Bottom toolbar: pinned to the bottom of the scrolling results pane
  // by the flex layout (root → flex-column, content → flex-grow-1, this
  // → flex-shrink-0). Surfaces the count + the "Vet visible" action
  // (VetToolbar owns the confirm + blocking-progress modals).
  const bottomToolbar = (
    <div className="d-flex align-items-center gap-2 p-2 border-top flex-shrink-0" style={TINT_STYLE}>
      <span className="small text-muted">
        {addableSelectedCount} selected · {flatRows.length} of {serverTotal} shown
      </span>
      <div className="flex-grow-1" />
      <VetToolbar
        vettableRows={vettableRows}
        vetting={vetting}
        progress={vetProgress}
        failedBatches={vetFailedBatches}
        onVetVisible={onVetVisible}
        onCancelVet={onCancelVet}
      />
    </div>
  );

  // Flex-column layout: top + bottom toolbars stay fixed; middle
  // scrolls. Pins the bottom toolbar to the bottom of the column
  // regardless of how few rows the body has (previously sticky-bottom
  // placed it just under the last row when content was short).
  const wrap = (body) => (
    <div className="d-flex flex-column h-100">
      {topToolbar}
      <div style={{ flex: 1, minHeight: 0, overflowY: "auto" }}>{body}</div>
      {bottomToolbar}
    </div>
  );

  if (flatRows.length === 0) {
    return wrap(
      <EmptyState icon="fa-filter-circle-xmark" title={`All ${serverTotal} matches are hidden by your filters`}>
        {filtersActive ? (
          <button type="button" className="btn btn-link p-0" onClick={clearFilters}>
            Clear filters
          </button>
        ) : (
          <span>Adjust the filters above.</span>
        )}
      </EmptyState>,
    );
  }

  if (mode === "by_outlet") {
    return wrap(
      filteredRows.map((group) => (
        <OutletGroupSection
          key={group.outlet.id}
          group={group}
          viewMode={viewMode}
          checked={checked}
          onToggleCheck={toggleCheck}
          setCheckedForIds={setCheckedForIds}
          focusedId={focusedId}
          onFocus={onFocus}
          existingIds={existingIds}
          verdicts={verdicts}
          verdictStaleFor={verdictStaleFor}
        />
      )),
    );
  }

  return wrap(
    viewMode === "cards" ? (
      <CardGrid>
        {flatRows.map((candidate) => (
          <RecipientCard
            key={candidate.id}
            candidate={candidate}
            checked={checked.has(candidate.id)}
            onToggle={() => toggleCheck(candidate.id)}
            alreadyAdded={existingIds.has(candidate.id)}
            isFocused={focusedId === candidate.id}
            onFocus={onFocus}
            showDistance={mode === "semantic"}
            verdict={verdicts[candidate.id]}
            stale={verdictStaleFor(candidate.id)}
          />
        ))}
      </CardGrid>
    ) : (
      flatRows.map((candidate) => (
        <ResultRow
          key={candidate.id}
          candidate={candidate}
          checked={checked.has(candidate.id)}
          onToggleCheck={toggleCheck}
          isFocused={focusedId === candidate.id}
          onFocus={onFocus}
          alreadyAdded={existingIds.has(candidate.id)}
        />
      ))
    ),
  );
}

// by_outlet section header + body. Body renders either a card grid or
// a list of ResultRow.
function OutletGroupSection({
  group,
  viewMode,
  checked,
  onToggleCheck,
  setCheckedForIds,
  focusedId,
  onFocus,
  existingIds,
  verdicts = {},
  verdictStaleFor = () => false,
}) {
  const addableIds = group.reporters.map((r) => r.id).filter((id) => !existingIds.has(id));
  const allChecked = addableIds.length > 0 && addableIds.every((id) => checked.has(id));
  const someChecked = !allChecked && addableIds.some((id) => checked.has(id));

  return (
    <div className="border-bottom mb-2">
      <div className="d-flex align-items-center gap-2 px-3 py-2 bg-light border-bottom">
        <Form.Check
          type="checkbox"
          checked={allChecked}
          ref={(el) => {
            // eslint-disable-next-line no-param-reassign
            if (el) el.indeterminate = someChecked;
          }}
          onChange={(e) => setCheckedForIds(addableIds, e.target.checked)}
          aria-label={`Select all reporters from ${group.outlet.name}`}
        />
        <span className="fw-semibold">{group.outlet.name}</span>
        {group.outlet.display_name && group.outlet.display_name !== group.outlet.name && (
          <span className="text-muted small ms-2">{group.outlet.display_name}</span>
        )}
        {group.outlet.prestige && group.outlet.prestige !== "base" && (
          <Badge bg="warning" text="dark" className="ms-2" style={{ fontSize: "0.65rem" }}>
            {group.outlet.prestige}
          </Badge>
        )}
        <div className="flex-grow-1" />
        {group.outlet.domain && <span className="text-muted small">{group.outlet.domain}</span>}
        <span className="text-muted small ms-2">
          {group.reporters.length} reporter{group.reporters.length === 1 ? "" : "s"}
        </span>
      </div>
      {viewMode === "cards" ? (
        <CardGrid>
          {group.reporters.map((reporter) => (
            <RecipientCard
              key={reporter.id}
              candidate={reporter}
              checked={checked.has(reporter.id)}
              onToggle={() => onToggleCheck(reporter.id)}
              alreadyAdded={existingIds.has(reporter.id)}
              isFocused={focusedId === reporter.id}
              onFocus={onFocus}
              verdict={verdicts[reporter.id]}
              stale={verdictStaleFor(reporter.id)}
            />
          ))}
        </CardGrid>
      ) : (
        group.reporters.map((reporter) => (
          <ResultRow
            key={reporter.id}
            candidate={{ ...reporter, match_type: "by_outlet" }}
            checked={checked.has(reporter.id)}
            onToggleCheck={onToggleCheck}
            isFocused={focusedId === reporter.id}
            onFocus={onFocus}
            alreadyAdded={existingIds.has(reporter.id)}
          />
        ))
      )}
      {group.outlet.total_reporter_count != null && group.outlet.total_reporter_count > group.outlet.reporter_count && (
        // Visibility footer for server-side truncation. The by_outlet
        // finder caps each outlet group at MAX_REPORTERS_PER_OUTLET = 50
        // for rendering performance; without surfacing the underlying
        // total here, admins at high-volume outlets would silently miss
        // reporters past the cap with no way of knowing they exist.
        // The by-name workaround is the realistic path when an admin
        // has a specific reporter in mind that didn't make the top 50.
        <div className="px-3 py-2 text-muted small border-top bg-light">
          Showing {group.outlet.reporter_count} of {group.outlet.total_reporter_count} reporters at{" "}
          <span className="fw-semibold">{group.outlet.name}</span>. To find a specific reporter not listed, search by
          name.
        </div>
      )}
    </div>
  );
}

// Centered empty state: large muted icon over a heading + body text.
// Used for the search-prompt, no-matches, refine-required, and
// filters-hide-all branches so they share the same visual rhythm.
function EmptyState({ icon, title, children }) {
  return (
    <div className="d-flex flex-column align-items-center justify-content-center text-muted p-5 h-100">
      <i className={`fa-solid ${icon} mb-3`} style={{ fontSize: "3rem", opacity: 0.35 }} aria-hidden="true" />
      <div className="text-center">
        <div className="fw-semibold mb-2">{title}</div>
        <div className="small">{children}</div>
      </div>
    </div>
  );
}
